import { Link } from 'react-router-dom'
import { Zap, BatteryWarning, PlugZap } from 'lucide-react'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { MetricLine } from '@/components/charts/MetricLine'
import { usePower, usePowerSeries, watts, kwh, money } from '@/lib/power'

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="text-xl font-semibold tabular-nums">{value}</div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </div>
  )
}

export function PowerCard() {
  const { data, isLoading } = usePower()
  const { data: series } = usePowerSeries('24h')

  if (isLoading || !data) return <Skeleton className="h-64" />

  const cur = data.currency
  const offline = !data.online
  // Pass the raw ISO timestamp — MetricLine's XAxis formats it itself
  // (it calls new Date(v)); pre-formatting here caused "Invalid Date".
  const chart = (series?.points ?? [])
    .filter((p) => p.power_w != null)
    .map((p) => ({ t: p.b, power_w: p.power_w }))

  const hp = data.month.by_band?.hp
  const hc = data.month.by_band?.hc

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="text-base flex items-center gap-2">
          <Zap className="h-4 w-4 text-primary" />
          Power — cluster UPS
        </CardTitle>
        <div className="flex items-center gap-3">
          {offline ? (
            <span
              className="flex items-center gap-1 text-xs text-destructive"
              title="Plug unreachable — possible mains outage (cluster on UPS battery)"
            >
              <BatteryWarning className="h-4 w-4" /> on battery?
            </span>
          ) : (
            <span className="flex items-center gap-1 text-xs text-emerald-500">
              <PlugZap className="h-4 w-4" /> mains
            </span>
          )}
          <Link to="/settings/power" className="text-xs text-muted-foreground hover:underline">
            Tariff
          </Link>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Stat label="Now" value={watts(data.power_w)} />
          <Stat
            label="Today"
            value={money(data.today.cost, cur)}
            sub={kwh(data.today.kwh)}
          />
          <Stat
            label="This month"
            value={money(data.month.cost, cur)}
            sub={kwh(data.month.kwh)}
          />
          <Stat
            label="Projected month"
            value={money(data.projection.month_cost, cur)}
            sub={`~${kwh(data.projection.month_kwh)} est.`}
          />
        </div>

        {/* Every figure on this card is energy only, so this breakdown sums to
            the "This month" total above and matches the Consumption history
            card exactly. The subscription is a flat fee and is excluded. */}
        {(hp || hc) && (
          <div className="text-xs text-muted-foreground">
            This month —{' '}
            {hp && <>HP {kwh(hp.kwh)} ({money(hp.cost, cur)})</>}
            {hp && hc && ' · '}
            {hc && <>HC {kwh(hc.kwh)} ({money(hc.cost, cur)})</>}
          </div>
        )}

        {chart.length > 1 && (
          <MetricLine
            data={chart}
            xKey="t"
            yKey="power_w"
            format={(v) => watts(typeof v === 'number' ? v : null)}
            label="Power (24h)"
            height={160}
          />
        )}
      </CardContent>
    </Card>
  )
}
